import {useLocation, useNavigate} from "react-router-dom";
import {Center, CloseButton, Container, Group, Loader, Paper, Space, Title} from "@mantine/core";
import {
  GeofenceSiteGroup,
  GeofenceSiteGroupEntity,
  geofenceSiteGroupSelector,
  useGetGeofenceSiteGroupQuery
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo, useState} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {
  GeofenceSiteGroupForm,
  GeofenceSiteGroupFormDefaultValues,
  GeofenceSiteGroupFormValues
} from "../../components/form/GeofenceSiteGroupForm/GeofenceSiteGroupForm";
import {useGeofenceSiteGroupMutation} from "../../hooks/useGeofenceSiteGroupMutation";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {ProtectedPage} from "../../components/page/ProtectedPage/ProtectedPage";

type Props = {
  id?: string;
  copy?: boolean;
  formDefaults?: GeofenceSiteGroupFormDefaultValues
}

export const GeofenceSiteGroupFormPageRoute = () => {
  const {state} = useLocation();
  const id = state?.id;
  const copy = state?.copy;
  const formDefaults = state?.formDefaults;
  return (
    <GeofenceSiteGroupFormPage
      id={id}
      copy={copy}
      formDefaults={formDefaults}
    />
  );
};

export const toFormDefaultsFromGeofenceSiteGroup = (geofenceSiteGroup: GeofenceSiteGroupEntity | undefined): GeofenceSiteGroupFormDefaultValues => {
  return {
    name: geofenceSiteGroup?.name,
  };
};

export const GeofenceSiteGroupFormPage = (props: Props) => {
  const {
    id,
    copy = false,
    formDefaults,
  } = props;

  const {regionId} = useApp()

  const navigate = useNavigate();
  const {t} = useTranslation(["common"]);

  const [access, setAccess] = useState(false);

  const selectGeofenceSiteGroup = useMemo(() => {
    return geofenceSiteGroupSelector()
  }, [])

  const {currGeofenceSiteGroup, isLoading} = useGetGeofenceSiteGroupQuery(access ? (id ?? skipToken) : skipToken, {
    selectFromResult: (result) => ({
      currGeofenceSiteGroup: selectGeofenceSiteGroup(result),
      isLoading: result.isLoading
    })
  });

  const _formDefaults: GeofenceSiteGroupFormDefaultValues = useMemo(() => {
    return formDefaults ? formDefaults : toFormDefaultsFromGeofenceSiteGroup(currGeofenceSiteGroup);
  }, [currGeofenceSiteGroup, formDefaults]);

  const isEdit = useMemo(() => {
    return currGeofenceSiteGroup && !copy
  }, [currGeofenceSiteGroup, copy])

  const title: string = useMemo(() => {
    if (isEdit) {
      return t("page.entity.geofencesitegroup.form.title.edit", "Edit Geofence Site Group", {ns: "common"});
    } else {
      return t("page.entity.geofencesitegroup.form.title.new", "New Geofence Site Group", {ns: "common"});
    }
  }, [isEdit, t]);

  const {
    createGeofenceSiteGroup,
    updateGeofenceSiteGroup,
    isCreating,
    isUpdating,
  } = useGeofenceSiteGroupMutation()

  const onGeofenceSiteGroupSubmit = useCallback((formValues: GeofenceSiteGroupFormValues) => {
    const patch: Partial<GeofenceSiteGroup> = {
      name: formValues.name,
    }

    if (isEdit) {
      const geofenceSiteGroup: Partial<GeofenceSiteGroup> = {
        ...currGeofenceSiteGroup.asTypeValue(),
        ...patch,
      };
      updateGeofenceSiteGroup(geofenceSiteGroup, {onSuccess: () => navigate(-1)})
    } else {
      const geofenceSiteGroup: Partial<GeofenceSiteGroup> = {
        ...patch,
      };
      createGeofenceSiteGroup(geofenceSiteGroup, {onSuccess: (payload) => navigate(`/geofence-site-groups/profile?id=${payload.id}`, {replace: true})})
    }
  }, [currGeofenceSiteGroup, isEdit, createGeofenceSiteGroup, updateGeofenceSiteGroup, navigate]);

  return (
    <ProtectedPage
      entity={currGeofenceSiteGroup}
      permissions={[isEdit ? "geofencesitegroup_edit" : "geofencesitegroup_create"]}
      isLoading={isLoading}
      onAccess={setAccess}>
      {(classes) => (
        <Container className={classes.container} fluid={true} py="md">
          <Center>
            {isLoading && <Loader/>}
            {!isLoading && (
              <Paper miw={500} shadow="sm" p="md" py="md">
                <Group position="apart">
                  <Title align="center">
                    {title}
                  </Title>
                  <CloseButton title="Close" size="xl" iconSize={20} onClick={() => navigate(-1)}/>
                </Group>
                <Space h="md"/>
                <GeofenceSiteGroupForm
                  options={{
                    regionId: regionId,
                  }}
                  defaultValues={_formDefaults}
                  submitting={isCreating || isUpdating}
                  onSubmit={onGeofenceSiteGroupSubmit}/>
              </Paper>
            )}
          </Center>
        </Container>
      )}
    </ProtectedPage>
  );
};

